import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import { EMPTY, from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { scrollIntoViewIfNeeded } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';
import { IntegrationKinds } from '@recruitee/integrations/types';
import { DialogService, DialogWidth, RtStatefulComponent } from '@recruitee/user-interface';

import { HttpErrorHandlerService } from '../../common';
import { takeUntilDestroyed } from '../../common/decorators/destroyable';
import { IntegrationEventActions } from '../../common/events/events';
import { UrlInTranslationsPipe } from '../../common/pipes/url-in-translations.pipe';
import { IntegrationUnion } from '../../common/types';
import { FieldMappingConfig } from '../../field-mapping/types';
import {
  IntegrateDialogFactory,
  IntegrateDialogTemplate,
} from '../../integrations/_generic/integrate-dialog.template';
import { IntegrationLegalDialogService } from '../integration-list-legal-dialog/integration-legal-dialog.service';
import { RoleAbilityMatrixComponent } from '../role-ability-matrix/role-ability-matrix.component';
import {
  Ability,
  IntegrateDialogConfig,
  IntegrationListItemBadge,
  IntegrationListItemConfig,
  LegalDialogConfig,
  RemoveIntegrationActionConfig,
  Role,
  RoleAbilityMap,
} from '../types';

type SharedState = {
  editing: boolean;
  saving: boolean;
  rolesForm: RoleAbilityMap;
};

@Directive()
export abstract class IntegrationListItem<State extends SharedState>
  extends RtStatefulComponent<State>
  implements OnInit, OnChanges
{
  @Input() public fieldMapping: FieldMappingConfig | null = null;

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected i18n: I18nService,
    protected dialogService: DialogService,
    protected route: ActivatedRoute,
    protected elementRef: ElementRef,
    protected injector: Injector,
    protected urlInTranslationsPipe: UrlInTranslationsPipe,
    protected store: Store,
    protected httpErrorHandlerService: HttpErrorHandlerService,
    protected integrationLegalDialogService: IntegrationLegalDialogService,
  ) {
    super(cdRef);
  }

  public get canSave(): boolean {
    return !this.actionsDisabled;
  }

  public get actionsDisabled(): boolean {
    return this.state.saving;
  }

  public get integrateLoading(): boolean {
    return false;
  }

  public get credentialized(): boolean {
    return !!this.integration;
  }

  public get config(): IntegrationListItemConfig {
    return {
      kind: this.kind,
      integration: this.integration,
      fieldMapping: this.fieldMapping,
      isNew: this.isNew,
      integrateLoading: this.integrateLoading,
      rolesForm: this.state.rolesForm,
      editing: this.state.editing,
      removeActionConfig: this.removeActionConfig,
      companyRoles: this.companyRoles,
      abilities: this.abilities,
      actionsDisabled: this.actionsDisabled,
      canSave: this.canSave,
      credentialized: this.credentialized,
      badge: () => this.badge(),
      credentialize: () => {
        this.credentialize();
      },
    };
  }

  public abstract get kind(): IntegrationKinds;

  public abstract get integration(): IntegrationUnion | null;

  public abstract get removeActionConfig(): RemoveIntegrationActionConfig;

  public abstract get companyRoles(): Role[];

  public abstract get abilities(): Ability[] | null;

  public abstract get isNew(): boolean;

  protected abstract get INITIAL_STATE(): State;

  protected get rolesChanged(): boolean {
    if (!this.integration) {
      return false;
    }

    return RoleAbilityMatrixComponent.roleAbilityMapChanged(
      this.state.rolesForm,
      this.integration.roles,
    );
  }
  public ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this)).subscribe(params => {
      if (this.config.integration && params['open_integration'] === this.config.kind) {
        this.startEditing();
        setTimeout(() => {
          scrollIntoViewIfNeeded(this.elementRef.nativeElement);
        }, 0);
      }
    });
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    if (this.config.integration) {
      this.initializeRolesForm();
    } else {
      this.setState(this.INITIAL_STATE);
    }
  }

  public startEditing(): void {
    this.setState({ ...this.state, editing: true });
  }

  public stopEditing(): void {
    this.setState({ ...this.state, editing: false });
    this.initializeRolesForm();
  }

  public updateRolesForm(roleAbilityMap: RoleAbilityMap): void {
    this.setState({ ...this.state, rolesForm: roleAbilityMap });
  }

  public credentialize(): void {
    return;
  }

  public badge(): IntegrationListItemBadge {
    return {
      label: this.i18n.translate('rti.common.settings.integrations.integrated'),
      color: 'success',
    };
  }

  public integrateWithGenericDialog(
    legalDialogConfig: LegalDialogConfig,
    integrateDialogConfig: Omit<IntegrateDialogConfig, 'dialogComponent'>,
  ): void {
    this.integrateWithDialog(legalDialogConfig, {
      ...integrateDialogConfig,
      dialogComponent: () =>
        import(
          './../../integrations/_generic/integrate-generic-dialog/integrate-generic-dialog.component'
        ).then(e => e.IntegrateGenericDialogComponent),
    });
  }

  public async changeCredentialsWithGenericDialog(
    context: () => IntegrateDialogTemplate,
  ): Promise<void> {
    const { IntegrateGenericDialogComponent } = await import(
      './../../integrations/_generic/integrate-generic-dialog/integrate-generic-dialog.component'
    );
    this.dialogService
      .openCustom<any, any, any>(IntegrateGenericDialogComponent, {
        width: DialogWidth.SmallXX,
        injector: this.injector,
        providers: [{ provide: IntegrateDialogFactory, useValue: context }],
      })
      .afterClosed.subscribe(integration => {
        if (integration) {
          this.store.dispatch(
            IntegrationEventActions.IntegrationUpdatedEvent({ payload: { uid: this.kind } }),
          );
        }
      });
  }

  public integrateWithDialog(
    legalDialogConfig: LegalDialogConfig,
    integrateDialogConfig: IntegrateDialogConfig,
  ): void {
    this.integrationLegalDialogService
      .openLegalDialog(this.kind, null, this.injector, legalDialogConfig)
      .pipe(
        switchMap((result: { accepted: boolean } | undefined) => {
          if (!result || !result.accepted) return EMPTY;
          return from(integrateDialogConfig.dialogComponent()).pipe(
            switchMap(
              component =>
                this.dialogService.openCustom(
                  component,
                  {
                    width: DialogWidth.SmallXX,
                    injector: this.injector,
                    providers: integrateDialogConfig.providers || [],
                  },
                  {},
                ).afterClosed,
            ),
          );
        }),
        tap((integration?: IntegrationUnion) => {
          if (integration) {
            integrateDialogConfig.handler(integration);
            this.startEditing();
            scrollIntoViewIfNeeded(this.elementRef.nativeElement);

            this.store.dispatch(
              IntegrationEventActions.IntegratedEvent({
                payload: { context: { integrationKind: this.config.kind } },
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  public abstract save(): void;
  public abstract integrate(): void;

  public remove(): void {
    this.dialogService
      .confirm(this.config!.removeActionConfig.dialogConfig)
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.config!.removeActionConfig.handler().subscribe(
          () => {
            if (this.config!.kind) {
              this.store.dispatch(
                IntegrationEventActions.DisintegratedEvent({
                  payload: { context: { integrationKind: this.config!.kind } },
                }),
              );
              this.store.dispatch(
                IntegrationEventActions.IntegrationRemovedEvent({
                  payload: { uid: this.config.kind },
                }),
              );
            }
          },
          errorResponse => {
            this.httpErrorHandlerService.default(errorResponse);
          },
        );
      });
  }

  private initializeRolesForm(): void {
    const rolesForm = RoleAbilityMatrixComponent.buildRolesForm(
      this.abilities || [],
      this.companyRoles,
      this.integration!.roles || {},
    );
    this.setState({ ...this.state, rolesForm });
  }
}
